import axios from 'axios';
import { authService } from './auth';
import {
  mockStats,
  mockGrowthData,
  mockSystemLogs,
  mockCancellations,
} from '../data/mockData';

// Simulate network delay
const delay = (ms = 500) => new Promise(resolve => setTimeout(resolve, ms));

// Configure axios (ready for real API)
const apiClient = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:8000/api',
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('admin_token');
      if (token) {
          // Remove any quotes if they exist
          const cleanToken = token.replace(/^"|"$/g, '');
          config.headers.Authorization = `Bearer ${cleanToken}`;  // No quotes here!
      }

      if (config.method === 'patch') {
          config.headers['Content-Type'] = 'application/merge-patch+json';
      }

      return config;
  },
  (error) => Promise.reject(error)
);

apiClient.interceptors.response.use(
    (response) => response,
    async (error) => {
        if (error.response?.status === 401) {
            // Token expired or invalid
            authService.clearAuthData();
            window.location.href = '/login';
        }
        return Promise.reject(error);
    }
);

// Mock API functions
export const api = {
  // ===== AUTHENTICATION =====
  async login(email, password) {
      try {
          // Call the real API endpoint
          const response = await apiClient.post('/login', {
              email: email,
              password: password
          });

          // Extract token and user data from response
          const { token, refresh_token } = response.data;

          if (token) {
              // Store tokens
              localStorage.setItem('admin_token', token);
              if (refresh_token) {
                  localStorage.setItem('admin_refresh_token', refresh_token);
              }

              // Decode JWT to extract user info
              try {
                  const tokenParts = token.split('.');
                  const payload = JSON.parse(atob(tokenParts[1]));

                  const user = {
                      email: payload.username || email,
                      roles: payload.roles || ['ROLE_USER'],
                      role: payload.roles?.includes('ROLE_ADMIN') ? 'super_admin' : 'user',
                      firstname: 'Admin', // You may want to fetch this from a /me endpoint
                      lastname: 'User',
                      id: 1
                  };

                  localStorage.setItem('admin_user', JSON.stringify(user));

                  return {
                      success: true,
                      data: {
                          token: token,
                          admin: user
                      }
                  };
              } catch (e) {
                  console.error('Error decoding token:', e);
                  // Still return success with minimal data
                  return {
                      success: true,
                      data: {
                          token: token,
                          admin: {
                              email: email,
                              role: 'super_admin'
                          }
                      }
                  };
              }
          }

          throw new Error('No token received');
      } catch (error) {
          console.error('Login error:', error);
          throw new Error(error.response?.data?.message || 'Identifiants incorrects');
      }
  },

  async logout() {
    await delay(200);
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_user');
    return { success: true };
  },

  async updateProfile(profileData) {
    await delay();
    // Simulate profile update
    const currentUser = JSON.parse(localStorage.getItem('admin_user') || '{}');
    const updatedUser = {
      ...currentUser,
      ...profileData,
    };
    localStorage.setItem('admin_user', JSON.stringify(updatedUser));
    return {
      success: true,
      data: updatedUser,
    };
  },

  async updatePassword(currentPassword, newPassword) {
    await delay();
    // Mock password validation (accept "admin123" as current password)
    if (currentPassword !== 'admin123') {
      throw new Error('Mot de passe actuel incorrect');
    }

    // In production, this would hash and update the password
    return {
      success: true,
      message: 'Mot de passe modifié avec succès',
    };
  },

  // ===== USERS =====
    async getUsers(filters = {}) {
        try {
            // Build query parameters for API Platform
            const params = new URLSearchParams();

            // Pagination
            const page = filters.page || 1;
            const limit = filters.limit || 25;
            params.append('page', page);
            params.append('itemsPerPage', limit);

            // Search filter (if your API supports it)
            if (filters.search) {
                params.append('email', filters.search);
                // Or use a custom filter if you've created one
                // params.append('search', filters.search);
            }

            // Status filter (you may need to adjust based on your API)
            if (filters.status && filters.status !== 'all') {
                // Assuming you have a subscription relation
                params.append('subscription.status', filters.status);
            }



            // Sorting
            if (filters.sortBy) {
                const order = filters.sortOrder === 'desc' ? 'desc' : 'asc';
                params.append('order[' + filters.sortBy + ']', order);
            }

            const response = await apiClient.get('/users?' + params.toString());

            const users = response.data.member || response.data['hydra:member'] || [];
            const total = response.data.totalItems || response.data['hydra:totalItems'] || 0;

            // Make sure users is an array
            if (!Array.isArray(users)) {
                console.error('Users is not an array:', users);
                return {
                    data: [],
                    pagination: {
                        total: 0,
                        page: page,
                        limit: limit,
                        totalPages: 0,
                    }
                };
            }

            const formatDate = (dateString) => {
                if (!dateString) return null;
                const date = new Date(dateString);
                return date.toISOString().split('T')[0]; // Returns YYYY-MM-DD format
            };
            const formatTime = (dateString) => {
                if (!dateString) return null;
                const date = new Date(dateString);
                return date.toTimeString().split(' ')[0]; // Returns HH:MM:SS format
            };

            // Transform data to match frontend expectations
            const transformedUsers = users.map(user => {
                // Get user logs sorted by timestamp (most recent first)
                const userLogs = user.userLogs || [];
                const sortedLogs = [...userLogs].sort((a, b) =>
                    new Date(b.timestamp) - new Date(a.timestamp)
                );

                // Get the last log for IP address and last login
                const lastLog = sortedLogs[0];
                const lastLogin = lastLog ? lastLog.timestamp : null;
                const lastIpAddress = lastLog ? lastLog.ipAddress : null;

                // Transform logs for the logs array
                const transformedLogs = sortedLogs.map(log => ({
                    id: log.id,
                    date: formatDate(log.timestamp),
                    time: formatTime(log.timestamp),
                    device: log.device,
                    action: log.action,
                    ip_address: log.ipAddress
                }));

                return {
                    id: user.id,
                    firstname: user.firstName || '',
                    lastname: user.lastName || '',
                    email: user.email,
                    created_at: formatDate(user.createdAt),
                    last_login: formatDate(lastLogin),
                    subscription_status: user.subscription?.status || 'none',
                    children: user.children || [],
                    ip_address: lastIpAddress || null,
                    logs: transformedLogs,
                    // Adresse de livraison
                    shippingAddress: user.shippingAddress || null,
                    shippingCity: user.shippingCity || null,
                    shippingZipCode: user.shippingZipCode || null,
                    phoneNumber: user.phoneNumber || null,
                };
            });


            return {
                data: transformedUsers,
                pagination: {
                    total: total,
                    page: page,
                    limit: limit,
                    totalPages: Math.ceil(total / limit),
                }
            };
        } catch (error) {
            console.error('Error fetching users:', error);
            throw new Error(error.response?.data?.message || 'Failed to fetch users');
        }
    },

    async getUserById(id) {
        try {
            const response = await apiClient.get(`/users/${id}`);
            const user = response.data;

            // Transformer la subscription pour le frontend
            let subscription = null;
            if (user.subscription) {
                const sub = user.subscription;
                const billingCycle = sub.billingCycle || 'monthly';
                const monthlyPrice = sub.plan?.price ? parseFloat(sub.plan.price) : 0;
                // Calculer le montant selon le cycle
                const amount = billingCycle === 'yearly' ? monthlyPrice * 12 : monthlyPrice;
                
                subscription = {
                    id: sub.id,
                    status: sub.status,
                    billing_cycle: billingCycle,
                    plan_name: sub.plan?.name || 'Inconnu',
                    amount: amount,
                    start_date: sub.startDate,
                    end_date: sub.endDate,
                    next_billing: sub.endDate, // ou calculer selon la logique métier
                    // Infos téléphone
                    has_hardware: sub.hasHardware || false,
                    hardware_payment_type: sub.hardwarePaymentType || null,
                };
            }

            return {
                id: user.id,
                firstname: user.firstName,
                lastname: user.lastName,
                email: user.email,
                created_at: user.createdAt,
                subscription_status: user.subscription?.status || 'none',
                subscription: subscription,
                children: user.children || [],
                // Adresse de livraison
                shippingAddress: user.shippingAddress || null,
                shippingCity: user.shippingCity || null,
                shippingZipCode: user.shippingZipCode || null,
                phoneNumber: user.phoneNumber || null,
            };
        } catch (error) {
            throw new Error(error.response?.data?.message || 'User not found');
        }
    },

    /**
     * Récupère les détails Stripe d'un utilisateur (vrais prix depuis Stripe)
     */
    async getUserStripeDetails(userId) {
        try {
            const response = await apiClient.get(`/admin/users/${userId}/stripe-details`);
            return response.data;
        } catch (error) {
            console.warn('Impossible de récupérer les détails Stripe:', error);
            return null;
        }
    },

    /**
     * Récupère le statut d'expédition d'un utilisateur
     */
    async getShipmentStatus(userId) {
        try {
            const response = await apiClient.get(`/admin/users/${userId}/shipment`);
            return response.data;
        } catch (error) {
            console.warn('Impossible de récupérer le statut d\'expédition:', error);
            return null;
        }
    },

    /**
     * Met à jour le statut d'expédition d'un utilisateur
     */
    async updateShipmentStatus(userId, shipmentData) {
        try {
            const response = await apiClient.post(`/admin/users/${userId}/shipment`, shipmentData);
            return response.data;
        } catch (error) {
            throw new Error(error.response?.data?.error || 'Erreur lors de la mise à jour de l\'expédition');
        }
    },

    /**
     * Marque un téléphone Presque DIY comme reçu.
     * Déclenche l'automation Brevo (email de confirmation au client)
     */
    async markPhoneReceived(userId) {
        try {
            const response = await apiClient.post(`/admin/users/${userId}/phone-received`);
            return response.data;
        } catch (error) {
            throw new Error(error.response?.data?.error || 'Erreur lors du marquage du téléphone comme reçu');
        }
    },

    /**
     * Crée un utilisateur avec option d'abonnement actif
     */
    async createUser(userData) {
        try {
            // Utiliser le nouvel endpoint admin qui permet de créer avec abonnement
            const response = await apiClient.post('/admin/users/create-with-subscription', {
                email: userData.email,
                firstName: userData.firstname,
                lastName: userData.lastname,
                password: userData.password,
                roles: userData.roles || ['ROLE_USER'],
                createSubscription: userData.createSubscription || false,
                planId: userData.planId || null,
                billingCycle: userData.billingCycle || 'monthly',
                phoneNumber: userData.phoneNumber || null,
                shippingAddress: userData.shippingAddress || null,
                shippingCity: userData.shippingCity || null,
                shippingZipCode: userData.shippingZipCode || null,
            });

            return {
                id: response.data.user.id,
                firstname: response.data.user.firstName,
                lastname: response.data.user.lastName,
                email: response.data.user.email,
                subscriptionCreated: response.data.subscriptionCreated,
                generatedPassword: response.data.generatedPassword,
                otpEmailSent: response.data.otpEmailSent,
                mustChangePassword: response.data.user.mustChangePassword,
            };
        } catch (error) {
            throw new Error(error.response?.data?.error || error.response?.data?.message || 'Erreur lors de la création');
        }
    },

    /**
     * Renvoyer un mot de passe temporaire (OTP) à un utilisateur existant
     */
    async resendUserOtp(userId) {
        try {
            const response = await apiClient.post(`/admin/users/${userId}/resend-otp`);
            return response.data;
        } catch (error) {
            throw new Error(error.response?.data?.error || error.response?.data?.message || 'Erreur lors de l\'envoi du mot de passe temporaire');
        }
    },

    /**
     * Récupère la liste des plans d'abonnement
     */
    async getSubscriptionPlans() {
        try {
            const response = await apiClient.get('/subscription/plans');
            const data = response.data;
            
            // Gérer les différents formats de réponse possibles
            if (Array.isArray(data)) {
                return data;
            }
            if (data.plans && Array.isArray(data.plans)) {
                return data.plans;
            }
            if (data['hydra:member'] && Array.isArray(data['hydra:member'])) {
                return data['hydra:member'];
            }
            if (data.member && Array.isArray(data.member)) {
                return data.member;
            }
            
            console.warn('Format de réponse inattendu pour les plans:', data);
            return [];
        } catch (error) {
            console.error('Erreur lors de la récupération des plans:', error);
            return [];
        }
    },

    /**
     * Active/désactive l'abonnement d'un utilisateur
     */
    async toggleUserSubscription(userId, data) {
        try {
            const response = await apiClient.post(`/admin/users/${userId}/subscription/toggle`, data);
            return response.data;
        } catch (error) {
            throw new Error(error.response?.data?.error || 'Erreur lors de la modification de l\'abonnement');
        }
    },

    async updateUser(id, userData) {
        try {
            const payload = {};

            if (userData.firstname !== undefined) {
                payload.firstName = userData.firstname;
            }
            if (userData.lastname !== undefined) {
                payload.lastName = userData.lastname;
            }
            if (userData.email !== undefined) {
                payload.email = userData.email;
            }
            if (userData.isActive !== undefined) {
                payload.isActive = userData.isActive;
            }
            if (userData.roles !== undefined) {
                payload.roles = userData.roles;
            }

            // Use PATCH instead of PUT to only update provided fields
            const response = await apiClient.patch(`/users/${id}`, payload);

            return {
                id: response.data.id,
                firstname: response.data.firstName,
                lastname: response.data.lastName,
                email: response.data.email,
            };
        } catch (error) {
            throw new Error(error.response?.data?.message || 'Failed to update user');
        }
    },

    async deleteUser(id) {
        try {
            await apiClient.delete(`/users/${id}`);
            return { success: true };
        } catch (error) {
            throw new Error(error.response?.data?.message || 'Failed to delete user');
        }
    },

    async suspendUser(id) {
        try {
            // PATCH requests in API Platform require application/merge-patch+json
            const response = await apiClient.patch(`/users/${id}`, {
                isActive: false
            });
            console.log(response.data);
            return response.data;
        } catch (error) {
            throw new Error(error.response?.data?.message || 'Failed to suspend user');
        }
    },

  // ===== SUBSCRIPTIONS =====
    async getSubscriptions(filters = {}) {
        try {
            const params = new URLSearchParams();

            // Pagination
            const page = filters.page || 1;
            const limit = filters.limit || 25;
            params.append('page', page);
            params.append('itemsPerPage', limit);

            // Status filter
            if (filters.status && filters.status !== 'all') {
                params.append('status', filters.status);
            }

            // Search filter (by user email)
            if (filters.search) {
                params.append('user.email', filters.search);
            }

            // Sorting
            if (filters.sortBy) {
                const order = filters.sortOrder === 'desc' ? 'desc' : 'asc';
                params.append('order[' + filters.sortBy + ']', order);
            }

            const response = await apiClient.get('/subscriptions?' + params.toString());

            // Fix: Check for both 'member' and 'hydra:member'
            const subscriptions = response.data.member || response.data['hydra:member'] || [];
            const total = response.data.totalItems || response.data['hydra:totalItems'] || 0;

            console.log('API Response:', response.data);
            console.log('Subscriptions count:', subscriptions.length);
            console.log('Total items:', total);

            const formatDate = (dateString) => {
                if (!dateString) return null;
                const date = new Date(dateString);
                return date.toISOString().split('T')[0];
            };

            // Transform data to match frontend expectations
            let transformedSubscriptions = subscriptions.map(sub => {
                // Get user name
                const user = sub.user || {};
                const userName = `${user.firstName || ''} ${user.lastName || ''}`.trim() || 'Unknown';

                // Get plan details (if available)
                const plan = sub.plan || {};
                const durationDays = plan.durationDays || 30;
                const type = durationDays >= 365 ? 'yearly' : 'monthly';
                const amount = plan.price ? parseFloat(plan.price) : 0;

                // Calculate next billing date
                let nextBilling = null;
                if (sub.status === 'active' && sub.startDate) {
                    const start = new Date(sub.startDate);
                    const next = new Date(start);
                    if (type === 'monthly') {
                        next.setMonth(next.getMonth() + 1);
                    } else {
                        next.setFullYear(next.getFullYear() + 1);
                    }
                    nextBilling = formatDate(next);
                }

                // Get last payment date from payments array
                const payments = sub.payments || [];
                const sortedPayments = [...payments].sort((a, b) =>
                    new Date(b.createdAt) - new Date(a.createdAt)
                );
                const lastPayment = sortedPayments[0]?.createdAt
                    ? formatDate(sortedPayments[0].createdAt)
                    : formatDate(sub.startDate);

                // Transform payments to transactions
                const transactions = payments.map(payment => {
                    // Normalize status to lowercase for consistency
                    const normalizedStatus = typeof payment.status === 'string'
                        ? payment.status.toLowerCase()
                        : 'unknown';

                    return {
                        id: payment.stripePaymentIntentId || payment['@id'] || `payment_${Math.random()}`,
                        date: formatDate(payment.createdAt),
                        amount: payment.amountCents / 100,
                        status: normalizedStatus,
                        payment_method: 'Carte bancaire'
                    };
                }).sort((a, b) => new Date(b.date) - new Date(a.date));

                return {
                    id: sub.id,
                    stripe_id: sub.stripeSubscriptionId || '',
                    user_id: user.id,
                    user_name: userName,
                    type: type,
                    amount: amount,
                    currency: plan.currency || 'EUR',
                    status: sub.status.toLowerCase(),
                    start_date: formatDate(sub.startDate),
                    next_billing: nextBilling,
                    last_payment: lastPayment,
                    transactions: transactions
                };
            });

            // Client-side filter by type if needed
            if (filters.type && filters.type !== 'all') {
                transformedSubscriptions = transformedSubscriptions.filter(
                    sub => sub.type === filters.type
                );
            }

            return {
                data: transformedSubscriptions,
                pagination: {
                    total: total,
                    page: page,
                    limit: limit,
                    totalPages: Math.ceil(total / limit),
                }
            };
        } catch (error) {
            console.error('Error fetching subscriptions:', error);
            throw new Error(error.response?.data?.message || 'Failed to fetch subscriptions');
        }
    },

  async getSubscriptionById(id) {
      try {
          // Try to find by stripe subscription ID first
          const response = await apiClient.get(`/subscriptions?stripeSubscriptionId=${id}`);
          let subscription = response.data['hydra:member']?.[0];

          // If not found, try by internal ID
          if (!subscription) {
              const directResponse = await apiClient.get(`/subscriptions/${id}`);
              subscription = directResponse.data;
          }

          if (!subscription) {
              throw new Error('Subscription not found');
          }

          const formatDate = (dateString) => {
              if (!dateString) return null;
              const date = new Date(dateString);
              return date.toISOString().split('T')[0];
          };

          // Get user info
          const user = subscription.user || {};
          const userName = `${user.firstName || ''} ${user.lastName || ''}`.trim();

          // Get plan details
          const plan = subscription.plan || {};
          const durationDays = plan.durationDays || 30;
          const type = durationDays >= 365 ? 'yearly' : 'monthly';
          const amount = plan.price ? parseFloat(plan.price) : 0;

          // Calculate next billing
          let nextBilling = null;
          if (subscription.status === 'ACTIVE' && subscription.startDate) {
              const start = new Date(subscription.startDate);
              const next = new Date(start);
              if (type === 'monthly') {
                  next.setMonth(next.getMonth() + 1);
              } else {
                  next.setFullYear(next.getFullYear() + 1);
              }
              nextBilling = formatDate(next);
          }

          // Get payments/transactions
          const payments = subscription.payments || [];
          const transactions = payments.map(payment => {
              // Normalize status to lowercase
              const normalizedStatus = typeof payment.status === 'string'
                  ? payment.status.toLowerCase()
                  : 'unknown';

              return {
                  id: payment.stripePaymentIntentId || payment['@id'] || `payment_${Math.random()}`,
                  date: formatDate(payment.createdAt),
                  amount: payment.amountCents / 100,
                  status: normalizedStatus, // Ensure lowercase
                  payment_method: 'Carte bancaire'
              };
          }).sort((a, b) => new Date(b.date) - new Date(a.date));

          const lastPayment = transactions[0]?.date || formatDate(subscription.startDate);

          return {
              id: subscription.id,
              stripe_id: subscription.stripeSubscriptionId || ``,
              user_id: user.id,
              user_name: userName,
              type: type,
              amount: amount,
              currency: plan.currency || 'EUR',
              status: subscription.status.toLowerCase(),
              start_date: formatDate(subscription.startDate),
              next_billing: nextBilling,
              last_payment: lastPayment,
              transactions: transactions,
              user: user.id ? {
                  id: user.id,
                  firstname: user.firstName,
                  lastname: user.lastName,
                  email: user.email
              } : null
          };
      } catch (error) {
          console.error('Error fetching subscription:', error);
          throw new Error(error.response?.data?.message || 'Subscription not found');
      }
  },

    async suspendSubscription(id) {
        try {
            // Find the subscription first

            // Update status to SUSPENDED
            const updateResponse = await apiClient.patch(`/subscriptions/${id}`, {
                status: 'suspended'
            });

            return updateResponse.data;
        } catch (error) {
            console.error('Error suspending subscription:', error);
            throw new Error(error.response?.data?.message || 'Failed to suspend subscription');
        }
    },

    async activateSubscription(id) {
        try {
            // Update status to active
            const updateResponse = await apiClient.patch(`/subscriptions/${id}`, {
                status: 'active'
            });

            return updateResponse.data;
        } catch (error) {
            console.error('Error activating subscription:', error);
            throw new Error(error.response?.data?.message || 'Failed to activate subscription');
        }
    },
    async cancelSubscription(id) {
        try {

            // Call the unsubscribe endpoint
            const unsubscribeResponse = await apiClient.post(`/subscription/unsubscribe/${id}`);

            // Return the response data
            return {
                success: true,
                status: unsubscribeResponse.data.status,
                endDate: unsubscribeResponse.data.endDate,
                message: unsubscribeResponse.data.message || 'Subscription cancelled successfully'
            };
        } catch (error) {
            console.error('Error cancelling subscription:', error);

            // Handle specific error messages from the backend
            const errorMessage = error.response?.data?.error ||
                error.response?.data?.message ||
                'Failed to cancel subscription';

            throw new Error(errorMessage);
        }
    },

  async getSubscriptionHistory(id) {
      try {
          const subscription = await this.getSubscriptionById(id);
          return subscription.payments;
      } catch (error) {
          console.error('Error fetching subscription history:', error);
          throw new Error(error.response?.data?.message || 'Failed to fetch subscription history');
      }
  },

  // ===== STATISTICS =====
    async getStats() {
        try {
            const response = await apiClient.get('/stats/statistics');
            return response.data;
        } catch (error) {
            console.error('Error fetching statistics:', error);
            // Fall back to mock data in development
            if (process.env.NODE_ENV === 'development') {
                await delay(500);
                return mockStats;
            }
            throw error;
        }
    },

    async getGrowthData(period = '12months') {
        try {
            const response = await apiClient.get('/stats/growth', {
                params: { period }
            });
            return response.data;
        } catch (error) {
            console.error('Error fetching growth data:', error);
            // Fall back to mock data in development
            if (process.env.NODE_ENV === 'development') {
                await delay(500);
                return mockGrowthData;
            }
            throw error;
        }
    },

    async getCancellations() {
        try {
            const response = await apiClient.get('/stats/cancellations');
            return response.data;
        } catch (error) {
            console.error('Error fetching cancellations:', error);
            // Fall back to mock data in development
            if (process.env.NODE_ENV === 'development') {
                await delay(500);
                return mockCancellations;
            }
            throw error;
        }
    },

  // ===== ADMINS =====
    async getAdmins() {
        try {
            const response = await apiClient.get('/admin/admins');
            return response.data;
        } catch (error) {
            console.error('Error fetching admins:', error);
            throw error;
        }
    },

    async createAdmin(adminData) {
        try {
            const response = await apiClient.post('/admin/admins', adminData);
            return response.data;
        } catch (error) {
            throw new Error(error.response?.data?.error || 'Failed to create admin');
        }
    },

    async updateAdmin(id, adminData) {
        try {
            const response = await apiClient.put(`/admin/admins/${id}`, adminData);
            return response.data;
        } catch (error) {
            throw new Error(error.response?.data?.error || 'Failed to update admin');
        }
    },

    async deleteAdmin(id) {
        try {
            await apiClient.delete(`/admin/admins/${id}`);
            return { success: true };
        } catch (error) {
            throw new Error(error.response?.data?.error || 'Failed to delete admin');
        }
    },

// ===== SYSTEM LOGS =====
    async getSystemLogs(filters = {}) {
        try {
            const response = await apiClient.get('/admin/logs', {
                params: filters
            });
            return response.data;
        } catch (error) {
            console.error('Error fetching system logs:', error);
            if (process.env.NODE_ENV === 'development') {
                await delay();
                // Return mock data with the same structure
                let filtered = [...mockSystemLogs];

                // Apply filters to mock data
                if (filters.admin_id) {
                    filtered = filtered.filter(log => log.admin_id === parseInt(filters.admin_id));
                }
                if (filters.action && filters.action !== 'all') {
                    filtered = filtered.filter(log => log.action === filters.action);
                }

                const page = filters.page || 1;
                const limit = filters.limit || 50;
                const startIndex = (page - 1) * limit;
                const endIndex = startIndex + limit;
                const paginated = filtered.slice(startIndex, endIndex);

                return {
                    data: paginated,
                    pagination: {
                        total: filtered.length,
                        page: page,
                        limit: limit,
                        totalPages: Math.ceil(filtered.length / limit),
                    }
                };
            }
            throw error;
        }
    },
  // ===== DASHBOARD =====
    async getDashboardData() {
        try {
            const response = await apiClient.get('/dashboard');

            // The backend now returns data in the expected format
            // No transformation needed
            return response.data;
        } catch (error) {
            console.error('Error fetching dashboard data:', error);

            // Fall back to mock data if API fails
            // This allows development to continue even if backend is down
            throw error;
        }
    },

    async createUserSubscription(userId, data) {
        try {
            const response = await apiClient.post(`/admin/users/${userId}/subscription`, {
                plan_id: data.planId,
                create_stripe: data.createStripe !== false,
                trial_days: data.trialDays || 0,
                payment_behavior: data.paymentBehavior || 'allow_incomplete',
            });
            return response.data;
        } catch (error) {
            console.error('Error creating subscription:', error);
            throw new Error(error.response?.data?.error || 'Failed to create subscription');
        }
    },

    async createManualSubscription(userId, data) {
        try {
            const response = await apiClient.post(`/admin/users/${userId}/subscription/manual`, {
                plan_id: data.planId,
                duration_days: data.durationDays,
                end_date: data.endDate,
            });
            return response.data;
        } catch (error) {
            console.error('Error creating manual subscription:', error);
            throw new Error(error.response?.data?.error || 'Failed to create subscription');
        }
    },
};

export default api;
